<template>
  <v-card outlined>
    <v-card-title class="d-flex align-center">
      <v-icon left class="mr-2">mdi-account-lock-outline</v-icon>
      {{ t('admConfig.rolesEditor.title') }}
      <v-chip v-if="hasChanges" color="warning" size="small" label class="ml-3">
        {{ t('admConfig.statusEditor.unsaved') }}
      </v-chip>
    </v-card-title>

    <v-card-text>
      <div class="text-body-2 text-medium-emphasis mb-4">
        {{ t('admConfig.rolesEditor.description') }}
      </div>

      <v-alert v-if="saveError" type="error" variant="tonal" density="compact" class="mb-4">
        {{ saveError }}
        <ul v-if="saveErrorDetails.length" class="ml-4 mt-1">
          <li v-for="(e, i) in saveErrorDetails" :key="i" class="text-body-2">{{ e }}</li>
        </ul>
      </v-alert>
      <v-alert v-if="saveSuccess" type="success" variant="tonal" density="compact" class="mb-4">
        {{ saveSuccess }}
      </v-alert>

      <!-- Profili -->
      <div class="text-subtitle-2 mb-2">{{ t('admConfig.rolesEditor.rolesTitle') }}</div>

      <v-alert
        v-if="!workingRoles.length"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        {{ t('admConfig.rolesEditor.empty') }}
      </v-alert>

      <div v-else class="roles-list mb-3">
        <div
          v-for="(role, index) in workingRoles"
          :key="index"
          class="role-row d-flex align-center flex-wrap pa-2 rounded"
        >
          <v-text-field
            :model-value="role.id"
            :label="t('admConfig.rolesEditor.id')"
            :hint="t('admConfig.rolesEditor.idHint')"
            :error-messages="idErrors(role, index)"
            density="compact"
            persistent-hint
            class="role-row__id"
            @update:model-value="v => renameRole(index, v)"
          />
          <v-text-field
            v-model="role.label"
            :label="t('admConfig.rolesEditor.label')"
            :error-messages="labelErrors(role)"
            density="compact"
            counter="40"
            class="role-row__label ml-sm-3"
          />
          <div class="d-flex align-center flex-wrap ml-sm-3" style="gap: 4px;">
            <v-chip size="x-small" label variant="tonal" color="indigo">
              {{ t('admConfig.rolesEditor.statusCount', { count: statusUsage(role.id).length }) }}
            </v-chip>
            <v-chip
              size="x-small"
              label
              variant="tonal"
              :color="userUsage(role.id).length ? 'teal' : 'grey'"
            >
              {{ t('admConfig.rolesEditor.userCount', { count: userUsage(role.id).length }) }}
            </v-chip>
          </div>
          <v-spacer />
          <v-btn
            icon="mdi-delete-outline"
            size="small"
            variant="text"
            color="error"
            :title="t('common.delete')"
            @click="askRemove(index)"
          />
        </div>
      </div>

      <v-btn
        size="small"
        variant="tonal"
        prepend-icon="mdi-plus"
        class="mb-6"
        @click="addRole"
      >
        {{ t('admConfig.rolesEditor.add') }}
      </v-btn>

      <!-- Policy -->
      <v-divider class="mb-4" />

      <div class="text-subtitle-2 mb-1">{{ t('admConfig.rolesEditor.policyTitle') }}</div>
      <div class="text-caption text-medium-emphasis mb-2">
        {{ t('admConfig.rolesEditor.policyDescription') }}
      </div>
      <v-radio-group v-model="workingPolicy" density="compact" hide-details class="mb-4">
        <v-radio value="readonly">
          <template v-slot:label>
            <div>
              <div class="text-body-2">{{ t('admConfig.rolesEditor.policyReadonly') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ t('admConfig.rolesEditor.policyReadonlyHint') }}
              </div>
            </div>
          </template>
        </v-radio>
        <v-radio value="hidden">
          <template v-slot:label>
            <div>
              <div class="text-body-2">{{ t('admConfig.rolesEditor.policyHidden') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ t('admConfig.rolesEditor.policyHiddenHint') }}
              </div>
            </div>
          </template>
        </v-radio>
      </v-radio-group>
    </v-card-text>

    <v-card-actions>
      <span v-if="blockingErrors.length" class="text-caption text-error ml-3">
        {{ blockingErrors[0] }}
      </span>
      <v-spacer />
      <v-btn variant="text" :disabled="!hasChanges || saving" @click="loadFromStore">
        {{ t('admConfig.statusEditor.discard') }}
      </v-btn>
      <v-btn
        color="primary"
        :loading="saving"
        :disabled="!hasChanges || blockingErrors.length > 0"
        @click="save"
      >
        {{ t('admConfig.save') }}
      </v-btn>
    </v-card-actions>

    <!-- Eliminazione: bloccata finché il profilo è in uso da uno stato o da un utente -->
    <v-dialog v-model="removeDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6">{{ t('admConfig.rolesEditor.removeTitle') }}</v-card-title>
        <v-card-text>
          <template v-if="removeBlockers.length">
            <v-alert type="warning" variant="tonal" density="compact">
              {{ t('admConfig.rolesEditor.removeBlocked', { role: removingRole?.label || removingRole?.id }) }}
            </v-alert>
            <ul class="ml-4 mt-3">
              <li v-for="(b, i) in removeBlockers" :key="i" class="text-body-2">{{ b }}</li>
            </ul>
          </template>
          <template v-else>
            {{ t('admConfig.rolesEditor.removeConfirm', { role: removingRole?.label || removingRole?.id }) }}
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="removeDialog = false">{{ t('common.cancel') }}</v-btn>
          <v-btn
            color="error"
            variant="text"
            :disabled="removeBlockers.length > 0"
            @click="confirmRemove"
          >
            {{ t('common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProjectStore } from '../stores/projects.js'
import { apiCall } from '../utils/api.js'
import { PRIVILEGED_ROLES, POLICY_READONLY } from '../utils/workflowAccess.js'

const { t } = useI18n()
const projectStore = useProjectStore()

// Stesso pattern imposto dal Backend in ValidateRole
const ID_PATTERN = /^[a-z][a-z0-9_-]{1,30}$/

const workingRoles = ref([])
const workingPolicy = ref(POLICY_READONLY)
const savedSnapshot = ref('')

const saving = ref(false)
const saveError = ref('')
const saveErrorDetails = ref([])
const saveSuccess = ref('')

const removeDialog = ref(false)
const removingIndex = ref(null)

// Utenti per profilo: serve a impedire di cancellare un profilo che qualcuno sta già usando.
// L'editor lo vede solo il superadmin, che ha accesso alla directory.
const users = ref([])

const removingRole = computed(() =>
  removingIndex.value === null ? null : workingRoles.value[removingIndex.value])

const snapshot = () => JSON.stringify({ roles: workingRoles.value, policy: workingPolicy.value })
const hasChanges = computed(() => snapshot() !== savedSnapshot.value)

// ---- utilizzi ----

/** Stati che dichiarano questo profilo, come "Importação · BL/em transito". */
const statusUsage = (roleId) => {
  if (!roleId) return []
  const out = []
  for (const type of projectStore.projectTypes) {
    for (const status of type.statusList || []) {
      if (Array.isArray(status.roles) && status.roles.includes(roleId)) {
        out.push(`${type.descr} · ${status.descr}`)
      }
    }
  }
  return out
}

const userUsage = (roleId) => (roleId ? users.value.filter(u => u.role === roleId) : [])

// ---- validazione ----

const idErrors = (role, index) => {
  const id = (role.id || '').trim()
  if (!id) return [t('admConfig.rolesEditor.errIdRequired')]
  if (!ID_PATTERN.test(id)) return [t('admConfig.rolesEditor.errIdPattern')]
  if (PRIVILEGED_ROLES.includes(id)) return [t('admConfig.rolesEditor.errIdReserved', { role: id })]
  if (workingRoles.value.some((r, i) => i !== index && (r.id || '').trim() === id)) {
    return [t('admConfig.rolesEditor.errIdDuplicate')]
  }
  return []
}

const labelErrors = (role) => {
  const label = (role.label || '').trim()
  if (!label) return [t('admConfig.rolesEditor.errLabelRequired')]
  if (label.length > 40) return [t('admConfig.rolesEditor.errLabelLong')]
  return []
}

const blockingErrors = computed(() => {
  const out = []
  workingRoles.value.forEach((role, index) => {
    out.push(...idErrors(role, index), ...labelErrors(role))
  })
  return [...new Set(out)]
})

const removeBlockers = computed(() => {
  const role = removingRole.value
  if (!role?.id) return []

  const out = statusUsage(role.id).map(s =>
    t('admConfig.rolesEditor.blockerStatus', { status: s }))
  out.push(...userUsage(role.id).map(u =>
    t('admConfig.rolesEditor.blockerUser', { user: u.username })))
  return out
})

// ---- azioni ----

const addRole = () => {
  workingRoles.value.push({ id: '', label: '' })
}

/**
 * Rinominare l'id di un profilo lo sgancia dagli stati che lo citavano: il Backend rifiuterebbe il
 * salvataggio con un riferimento orfano. Gli stati si aggiornano insieme, in memoria — poi li
 * riscrive l'editor stati al suo prossimo salvataggio, o questo stesso salvataggio se sono già
 * caricati. Perciò qui si rinomina soltanto quando il vecchio id non è in uso.
 */
const renameRole = (index, value) => {
  const next = (value || '').trim()
  const previous = workingRoles.value[index].id
  workingRoles.value[index].id = next

  if (previous && previous !== next && statusUsage(previous).length) {
    saveError.value = t('admConfig.rolesEditor.renameWarning', { role: previous })
  }
}

const askRemove = (index) => {
  removingIndex.value = index
  removeDialog.value = true
}

const confirmRemove = () => {
  workingRoles.value.splice(removingIndex.value, 1)
  removeDialog.value = false
  removingIndex.value = null
}

function loadFromStore() {
  const config = projectStore.configurations?.config || {}
  workingRoles.value = Array.isArray(config.roles)
    ? JSON.parse(JSON.stringify(config.roles))
    : []
  workingPolicy.value = config.roleAccess?.outsideStatuses || POLICY_READONLY
  savedSnapshot.value = snapshot()
  saveError.value = ''
  saveErrorDetails.value = []
  saveSuccess.value = ''
}

const save = async () => {
  saving.value = true
  saveError.value = ''
  saveErrorDetails.value = []
  saveSuccess.value = ''
  try {
    const roles = workingRoles.value.map(r => ({
      id: (r.id || '').trim(),
      label: (r.label || '').trim()
    }))
    // Solo profili e policy: tipi di processo e azioni IA restano quelli dell'ultimo salvataggio
    const result = await projectStore.saveCfgParts({
      roles,
      roleAccess: { outsideStatuses: workingPolicy.value }
    })
    loadFromStore()
    saveSuccess.value = t('admConfig.rolesEditor.saved', {
      count: roles.length,
      backup: result?.backup || '—'
    })
  } catch (e) {
    saveError.value = e?.message || t('admConfig.statusEditor.saveError')
    const details = e?.response?.data?.errors
    if (Array.isArray(details)) saveErrorDetails.value = details
  } finally {
    saving.value = false
  }
}

const loadUsers = async () => {
  try {
    const response = await apiCall('/api/users/directory')
    users.value = response?.users || []
  } catch (e) {
    // Senza la directory si perde solo il conteggio degli utenti per profilo: l'editor resta usabile
    console.warn('Lista utenti non disponibile per il conteggio dei profili:', e?.message)
  }
}

loadFromStore()
loadUsers()
</script>

<style scoped>
.roles-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.role-row {
  gap: 8px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.role-row__id {
  flex: 1 1 180px;
  min-width: 160px;
}

.role-row__label {
  flex: 1 1 200px;
  min-width: 160px;
}
</style>
